import { axiosInstance } from "authProvider";
import { API_URL } from "../../constants";

export const getOuts = () => async (): Promise<[]> => {
  return await axiosInstance
    .get(`${API_URL}/api/ins?populate=*`)
    .then(({ data }) => data.data);
};

export const getReceiptById =
  (id: number) =>
  async (): Promise<{
    day: string;
    id: number;
    publishedAt: null;
    receipts: {
      amount_given: number | string;
      amount_received: number | string;
      client_id: number | string;
      containers: any[];
      receipt_number: number | string;
    }[];
  }> => {
    return await axiosInstance
      .get(`${API_URL}/api/receipts/${id}`)
      .then(({ data }) => data);
  };

export const bulkPayReceipts = async (
  data: any
): Promise<{
  data: {
    totalDifference: number;
  };
}> => {
  return await axiosInstance
    .post(`${API_URL}/api/receipts/bulk-update`, {
      data,
    })
    .then((data) => data);
};

export const getMonths = async (): Promise<any> => {
  return await axiosInstance
    .post(`${API_URL}/api/receipts/get-months`)
    .then((data) => data);
};

export const searchForReceiptsPerMonth = async (params: {
  month: string;
  receipt_number: number;
}): Promise<any> => {
  return await axiosInstance
    .get(
      `${API_URL}/api/receipts/search-receipts-by-number?month=${params?.month}&receipt_number=${params?.receipt_number}`
    )
    .then((data) => data);
};

export const getReicepts = async ({
  query,
}: {
  query: {
    from: string;
    to: string;
    includeDays: boolean;
  };
}): Promise<any> => {
  return await axiosInstance
    .get(
      `${API_URL}/api/receipts/get-receipts?from=${query?.from}&to=${query?.to}&includeDays=${query.includeDays}`
    )
    .then((data) => data);
};

export const searchByContainer = async ({
  query,
}: {
  query: {
    containerId: string;
    from: string | null;
    to: string | null;
  };
}): Promise<any> => {
  return await axiosInstance
    .get(`${API_URL}/api/receipts/get-by-container`, {
      params: {
        container_id: query?.containerId,
        from: query?.from,
        to: query?.to,
      },
    })
    .then((data) => data);
};

export const getSelectedReiceptsByPeriod = async ({
  query,
}: {
  query: {
    from: string;
    to: string;
  };
}): Promise<any> => {
  return await axiosInstance
    .get(
      `${API_URL}/api/receipts/get-receipts-by-period?from=${query?.from}&to=${query?.to}`
    )
    .then((data) => data);
};

export interface ReceiptReportsParams {
  from?: string;
  to?: string;
  groupBy?: 'day' | 'month' | 'client' | 'payment_method';
  clientId?: string;
  status?: 'paid' | 'unpaid';
  paymentMethod?: string;
}

export interface ReceiptReportSummary {
  totalReceipts: number;
  totalAmount: number;
  totalReceived: number;
  totalBalance: number;
  paidCount: number;
  unpaidCount: number;
  paymentMethods: Record<string, { count: number; amount: number }>;
}

export interface ReceiptData {
  id: string;
  date: string;
  receiptNumber: string;
  clientId: number;
  clientName: string;
  amountGiven: number;
  amountReceived: number;
  balance: number;
  status: 'paid' | 'unpaid';
  paymentMethod: string;
  containers: any[];
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptReportsResponse {
  summary: ReceiptReportSummary;
  data: any[];
}

export const getReceiptReports = async (params: ReceiptReportsParams): Promise<ReceiptReportsResponse> => {
  // Build query string from params
  const queryParams = new URLSearchParams();
  
  if (params.from) queryParams.append('from', params.from);
  if (params.to) queryParams.append('to', params.to);
  if (params.groupBy) queryParams.append('groupBy', params.groupBy);
  if (params.clientId) queryParams.append('clientId', params.clientId);
  if (params.status) queryParams.append('status', params.status);
  if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
  
  return await axiosInstance
    .get(`${API_URL}/api/receipts/reports?${queryParams.toString()}`)
    .then(({ data }) => data);
};

export const uploadReceiptImages = async (files: File[]): Promise<any> => {
  const formData = new FormData();
  
  // Append all files to FormData
  files.forEach((file) => {
    formData.append('files', file);
  });
  
  return await axiosInstance
    .post(`${API_URL}/api/receipts/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(({ data }) => data);
};

export const uploadReceiptPdfs = async (files: File[]): Promise<any> => {
  const formData = new FormData();
  
  // Append all PDF files to FormData
  files.forEach((file) => {
    formData.append('files', file);
  });
  
  return await axiosInstance
    .post(`${API_URL}/api/receipts/upload-pdf`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(({ data }) => data);
};

export const uploadReceiptPdfsOcr = async (files: File[]): Promise<any> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  return await axiosInstance
    .post(`${API_URL}/api/receipts/upload-pdf-ocr`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(({ data }) => data);
};
